import { Card, Divider, Flex, SimpleTable, Typography } from "djuno-design";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;
const DividerPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Divider</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Divider component in the Djuno Design system is a simple and
            efficient tool for separating content. It can visually break
            sections, group information, or highlight a transition between
            different contexts in the user interface. The Divider component
            supports various customization options such as types (solid, dashed,
            dotted) and orientation, with or without text.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Divider component offers several visual styles to adapt to
            different design needs. You can choose between:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Simple
                </Text>
                : A plain, solid line useful for basic content separation.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Dashed
                </Text>
                : A dashed line that provides a less rigid separation, often
                used for less important content boundaries.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Dotted
                </Text>
                : A dotted line that provides a more playful or light visual
                appearance.
              </Text>
            </li>
          </ul>
          <Text size="sm" className="mt-4">
            Additionally, Dividers can be customized to include text labels and
            can be aligned left, center, or right, to suit different layout
            needs.
          </Text>

          <Title level={5} className="mt-4">
            Additional Features
          </Title>
          <Text size="sm">
            The Divider component supports advanced features such as text
            inclusion within the divider, as well as orientation to position the
            text on the left, center, or right. These options are useful when
            needing to indicate transitions between sections with contextual
            meaning.
          </Text>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="w-full">
          <Title level={5} className="mt-5">
            Examples
          </Title>

          <Flex
            direction={{ default: "col", lg: "row" }}
            className="gap-4 w-full"
          >
            <ExpandableCard
              title="Divider"
              codeViewerProps={{
                code: DividerExample1,
              }}
            >
              <Flex className="gap-3 w-full">
                <Divider />
              </Flex>
              <Flex className="gap-3 w-full">
                <Divider uiType="simple" text="simple" />
              </Flex>
              <Flex className="gap-3 w-full">
                <Divider uiType="dashed" text="dashed" />
              </Flex>
              <Flex className="gap-3 w-full">
                <Divider uiType="dotted" text="dotted" />
              </Flex>
              <Flex className="gap-3 w-full">
                <Divider text="left" orientation="left" />
              </Flex>
              <Flex className="gap-3 w-full">
                <Divider text="center" orientation="center" />
              </Flex>
              <Flex className="gap-3 w-full">
                <Divider text="right" orientation="right" />
              </Flex>
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* uiType */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiType</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Type of divider</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Divider.ts#L35"}
                        size="xs"
                        code
                      >
                        DividerTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">simple</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* text */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">text</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Text displayed in the divider</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* orientation */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">orientation</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Position of the text</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Divider.ts#L36"}
                        size="xs"
                        code
                      >
                        OrientationTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">center</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* children */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">children</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">The content inside the divider</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">center</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* textClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">textClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Additional classes to apply to the text
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">center</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const DividerExample1 = `<Flex items={{ default: "center" }} className="gap-8">
  <Flex direction="col" items="center" className="gap-1">
    <Tooltip content="default content">
      <Text>default</Text>
    </Tooltip>
  </Flex>
  <Flex direction="col" items="center" className="gap-1">
    <Tooltip content="ckickable content" clickable>
      <Text>ckickable</Text>
    </Tooltip>
  </Flex>
</Flex>`;

export default DividerPage;
